/**
 * Edit User Form Composer
 *
 * Form for editing existing users with role and organization assignments.
 * - All fields pre-populated with existing data
 * - Enable/Disable account toggle: sets userStatus to 'active' or 'suspended'
 * - Resend Invitation button (only while the invitation is `pending` server-side,
 *   which includes lazily-expired ones — the invitation status, not user_status)
 * - Profile picture upload and Azure Entra ID fetch
 * - userStatus only ever set to active/suspended here; there is no
 *   'invitationPending' user status in the backend
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  EditUserFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type EditUserFormData,
} from './schema'
import { AddUserFormSchema, type AddUserFormData } from '../addUser/schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderButtonControl,
  renderSelectControl,
  type ControlConfig
} from '@/forms/formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue, validateBackendPhone } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { UserCog } from 'lucide-react'
import { UserStatus, InvitationStatus } from '@/types/users'
import { AppLogger } from '@/lib/logger'
import { renderMediaUploaderControl } from '@/forms/formRenderFunctions'
import { PhoneInputField } from '@/components/ui/phone-input'
import { useConfig } from '@/contexts/ConfigContext'
import { useToast } from '@/contexts/ToastContext'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type EditUserComposerProps = {
  userNumber: string
  currentUserStatus: UserStatus
  currentInvitationStatus?: InvitationStatus
  // The invitation's invitationNumber (from getInvitationList), set only while it's resendable
  // (`pending`). The resend endpoint keys on :invitationNumber, not userNumber.
  invitationNumber?: number
  initialValues: AddUserFormData & { userNumber?: number }
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

// Type guard for accessing custom YAML config properties
type EditUserFormConfig = typeof FORM_CONFIG & {
  accountStatus?: {
    label?: string
    activeMessage?: string
    inactiveMessage?: string
  }
  invitationStatus?: {
    label?: string
    pendingMessage?: string
    sentMessage?: string
  }
  resendInvitationButton?: {
    label?: string
    loadingLabel?: string
  }
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof EditUserFormData, value: unknown): string | null => {
  const shape = EditUserFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function EditUserComposer(props: EditUserComposerProps) {
  const {
    userNumber,
    currentUserStatus,
    currentInvitationStatus,
    invitationNumber,
    initialValues,
    onSuccess,
    onError,
    onCancel,
    showHeader = true,
    submitLabel
  } = props
  const config = useConfig()
  const { toast } = useToast()

  // Track account enabled state (Active vs Suspended)
  const normalizedStatus = (typeof currentUserStatus === 'string' ? currentUserStatus : '').toLowerCase() as UserStatus | ''
  const [isAccountEnabled, setIsAccountEnabled] = React.useState<boolean>(
    normalizedStatus === UserStatus.Active
  )

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)
  const editConfig = FORM_CONFIG as EditUserFormConfig

  const isRequiredField = (name: string) => {
    return ['firstName', 'lastName', 'emailAddress'].includes(name)
  }

  // Fetch all select options with a single hook call
  const { optionsMap, loadingMap } = useSelectOptionsMap({
    organizations: {
      endpoint: 'getOrganizations',
      dataKey: 'organizations',
      valueField: 'orgNumber',
      labelField: 'name',
    },
  })

  const upsertUser = useApiMutation('upsertUser', 'POST', { invalidates: ['getUsers', 'getUserByEmail', 'getUserByNumber'] })
  // Org association now goes through memberships (backend v1.0.36 retired user_organizations).
  // Note: the backend rejects a membership when the org's Farmgrid account is INACTIVE
  // (409 FARMGRID_ACCOUNT_INACTIVE); `active` and `unpaid` are allowed. Invalidate
  // getUserByNumber so the user's Organizations card refreshes after add/remove.
  const addOrgMembershipMutation = useApiMutation('upsertOrganizationMembership', 'POST', { invalidates: ['getUsers', 'getUserByEmail', 'getUserByNumber', 'getMemberships'] })
  const archiveOrgMembershipMutation = useApiMutation('archiveOrganizationMembership', 'DELETE', { invalidates: ['getUsers', 'getUserByEmail', 'getUserByNumber', 'getMemberships'] })
  // The user's current memberships — used to map org → membershipNumber for removal.
  const { data: membershipsData, isLoading: membershipsLoading } = useApiQuery('getMemberships', { userNumber: Number(userNumber) }, {})

  const resendInvitation = useApiMutation('resendInvitation', 'POST', {
    invalidates: ['getUsers', 'getInvitationList'],
    onSuccess: () => {
      toast({ title: 'Invitation resent', description: `Invitation resent to ${initialValues.emailAddress}.`, type: 'success' })
      onSuccess?.()
    },
    onError: (error) => {
      log.error('Failed to resend invitation:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: EditUserFormSchema,
    defaultValues: {
      firstName: initialValues.firstName ?? '',
      lastName: initialValues.lastName ?? '',
      emailAddress: initialValues.emailAddress ?? '',
      phone: initialValues.phone ?? '',
      organizations: initialValues.organizations ?? [],
      tenants: (initialValues as any)?.tenants ?? ['conex'],
      profilePicture: initialValues.profilePicture ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as EditUserFormData
      // Coerce and validate with Zod schema
      const coerced = {
        firstName: formValue.firstName?.trim() ?? '',
        lastName: formValue.lastName?.trim() ?? '',
        emailAddress: formValue.emailAddress?.trim() ?? '',
        phone: formValue.phone || undefined,
        organizations: Array.isArray(formValue.organizations) ? formValue.organizations : [],
        tenants: Array.isArray(formValue.tenants) && formValue.tenants.length > 0 ? formValue.tenants : ['conex'],
        // Use uploaded file objectKey or existing value
        profilePicture: formValue.profilePicture || undefined,
      }

      const parsed = AddUserFormSchema.safeParse(coerced)

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof EditUserFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        log.error('[EditUserComposer] Validation failed:', parsed.error)
        return
      }

      // Backend builds userName as `${firstName} ${lastName}` and validates it as
      // 3-30 chars. firstName/lastName are each max 20, so the combined value can
      // exceed 30 — guard here so it never reaches the backend (schema untouched).
      const userName = `${parsed.data.firstName} ${parsed.data.lastName}`
      if (userName.length < 3 || userName.length > 30) {
        const target: keyof EditUserFormData =
          parsed.data.lastName.length >= parsed.data.firstName.length ? 'lastName' : 'firstName'
        form.setFieldMeta(target, (meta) => ({
          ...meta,
          isTouched: true,
          errors: ['First and last name together must be 3-30 characters'],
        }))
        toast({
          title: 'Name too long',
          description: 'First and last name together must be between 3 and 30 characters.',
          type: 'error',
        })
        log.error('[EditUserComposer] userName length out of range')
        return
      }

      // Phone must satisfy the backend mobileNumber/countryCode contract.
      const phoneError = validateBackendPhone(parsed.data.phone)
      if (phoneError) {
        form.setFieldMeta('phone', (meta) => ({ ...meta, isTouched: true, errors: [phoneError] }))
        return
      }

      try {
        const baseRequest = transformToApiRequest(parsed.data)

        // Preserve/update status based on toggle
        // API validation expects Title Case: "Active" | "Suspended" | "Archived"
        // Backend converts to UPPERCASE for database storage
        const request = {
          ...baseRequest,
          // userNumber is the 10-digit business key the backend uses to identify existing users
          ...(initialValues.userNumber !== undefined && { userNumber: initialValues.userNumber }),
          // Backend upsertUser schema validates userStatus as the lowercase enum
          // ["active","suspended"] — send lowercase to match (frontend mirrors backend).
          userStatus: isAccountEnabled ? 'active' : 'suspended',
          // Backend requires at least one tenant (conex/farmgrid); selected by the user.
          tenants: parsed.data.tenants && parsed.data.tenants.length > 0 ? parsed.data.tenants : ['conex'],
        }

        const selectedOrganizations = parsed.data.organizations || []
        const previousOrganizations = initialValues.organizations || []

        // Update the user
        const userResponse = await upsertUser.mutateAsync(request)

        // Extract userNumber from response (API returns { message, content: { user: {...}, operation } })
        // Coerce to number — backend may return it as a string
        const rawUserNumber = userResponse?.content?.user?.userNumber
        const userNumber = rawUserNumber != null ? Number(rawUserNumber) : initialValues.userNumber

        if (!userNumber) {
          log.warn('[EditUserComposer] userNumber not available — skipping role/org changes')
          onSuccess?.()
          return
        }

        // Job roles were removed from the edit flow (legacy job-role removal). A user's
        // product access is granted via account memberships on the user detail page instead.

        // Calculate organization changes
        const organizationsToAdd = selectedOrganizations.filter(
          (org) => !previousOrganizations.includes(org)
        )
        const organizationsToRemove = previousOrganizations.filter(
          (org) => !selectedOrganizations.includes(org)
        )

        // Add new organizations via memberships (body { userNumber }; orgNumber is the path).
        if (organizationsToAdd.length > 0) {
          await Promise.all(
            organizationsToAdd.map((orgNumber) =>
              addOrgMembershipMutation.mutateAsync({
                orgNumber: parseInt(orgNumber, 10),
                userNumber,
              })
            )
          )
        }

        // Remove old organizations by archiving their membership. archiveOrganizationMembership
        // keys on membershipNumber, so map each org to the user's current membership. Don't skip
        // silently: if memberships haven't loaded or an org has no matching membership, fail
        // loudly rather than report a success that didn't remove anything.
        if (organizationsToRemove.length > 0) {
          const rawMemberships = (membershipsData as { content?: { memberships?: any[] } } | undefined)?.content?.memberships
          if (!Array.isArray(rawMemberships)) {
            throw new Error("Couldn't load this user's memberships to remove organizations. Please try again.")
          }
          const orgToMembership = new Map<number, number>(
            rawMemberships.map((m) => [Number(m.organization?.orgNumber), Number(m.membershipNumber)] as [number, number])
          )
          await Promise.all(
            organizationsToRemove.map((orgNumber) => {
              const orgNum = parseInt(orgNumber, 10)
              const membershipNumber = orgToMembership.get(orgNum)
              if (!membershipNumber) {
                throw new Error(`Couldn't find the membership to remove for organization ${orgNum}.`)
              }
              return archiveOrgMembershipMutation.mutateAsync({ orgNumber: orgNum, membershipNumber })
            })
          )
        }

        onSuccess?.()
      } catch (error) {
        log.error('[EditUserComposer] Submission error:', error)
        onError?.(error as Error)
      }
    },
  })

  // Handle resend invitation
  const handleResendInvitation = async () => {
    // POST /uniq/invitations/:invitationNumber/resend — keys on the invitation, not the user.
    if (invitationNumber == null) {
      log.warn('[EditUserComposer] Resend skipped — no pending invitation for this user')
      return
    }
    try {
      await resendInvitation.mutateAsync({ invitationNumber })
    } catch (error) {
      log.error('[EditUserComposer] Resend invitation error:', error)
    }
  }

  // The route only passes invitationNumber while the invitation is pending (resendable).
  const showResendInvitation = invitationNumber != null

  // Render a single text control
  const renderTextControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onChange: ({ value }) =>
            validateField(
              control.name as keyof AddUserFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof AddUserFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            type: control.inputType,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render a single select control
  const renderSelectControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)
    const isLoading = loadingMap[control.name]

    const options = control.optionsEndpoint
      ? optionsMap[control.name] || []
      : FormConfigLoader.normalizeSelectOptions(control.selectOptions)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onChange: ({ value }) =>
            validateField(
              control.name as keyof EditUserFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof EditUserFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            options: options,
            isLoading: isLoading,
          }
          return renderSelectControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render multi-select control (using checkboxes)
  const renderMultiSelectControlField = (control: any) => {
    const options = control.optionsEndpoint
      ? (optionsMap[control.name] || [])
      : FormConfigLoader.normalizeSelectOptions(control.selectOptions)
    const isLoading = loadingMap[control.name]

    return (
      <form.AppField
        key={control.name}
        name={control.name}
      >
        {(bound) => {
          const currentValue = (bound.state.value as string[]) || []

          const handleCheckboxChange = (itemId: string, checked: boolean) => {
            const current = Array.isArray(currentValue) ? currentValue : []
            const updated = checked
              ? [...current, itemId]
              : current.filter((id) => id !== itemId)
            bound.handleChange(updated)
          }

          return (
            <div className="space-y-2">
              <Label>
                {control.label}
                {control.required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
              </Label>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading {control.label.toLowerCase()}...</p>
              ) : options.length === 0 ? (
                <p className="text-sm text-muted-foreground">No {control.label.toLowerCase()} available</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {options.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${control.name}-${option.value}`}
                        checked={Array.isArray(currentValue) && currentValue.includes(option.value)}
                        onCheckedChange={(checked) => handleCheckboxChange(option.value, checked === true)}
                      />
                      <Label
                        htmlFor={`${control.name}-${option.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
              {bound.state.meta?.errors && bound.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">{bound.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.AppField>
    )
  }

  // Render control based on type
  const renderControlField = (control: any) => {
    if (control.htmlControl === 'phone') {
      return (
        <form.AppField
          key={control.name}
          name={control.name}
          validators={{
            // Mirror the backend contract (mobileNumber = exactly 10 digits after
            // a 1-3 digit country code) so an invalid number is caught before submit.
            // Validate live (onChange) too — PhoneInputField emits via onChange, so a
            // short/invalid number shows the message immediately instead of staying silent.
            onChange: ({ value }) => validateBackendPhone(value) || undefined,
            onBlur: ({ value }) => validateBackendPhone(value) || undefined,
          }}
        >
          {(bound) => (
            <PhoneInputField
              label={control.label}
              value={bound.state.value as string | undefined}
              onChange={(val) => bound.handleChange(val ?? '')}
              onBlur={bound.handleBlur}
              error={bound.state.meta.errors?.[0] as string | undefined}
            />
          )}
        </form.AppField>
      )
    }
    if (control.htmlControl === 'multiselect') {
      return renderMultiSelectControlField(control)
    }
    if (control.htmlControl === 'file') {
      // Use new MediaUploaderField pattern via renderMediaUploaderControl
      // Enable mockMode in development when backend upload API is not available
      const useMockUpload = config.appEnv === 'development'
      return (
        <form.AppField key={control.name} name={control.name}>
          {(bound) => renderMediaUploaderControl(control, bound, userNumber, useMockUpload)}
        </form.AppField>
      )
    }
    if (control.htmlControl === 'select') {
      return renderSelectControlField(control)
    }
    return renderTextControlField(control)
  }

  const formBody = (
    <>
      {/* Account Status Toggle (only for Active/Suspended users) */}
      {(normalizedStatus === UserStatus.Active || normalizedStatus === UserStatus.Suspended) && (
        <div className="flex items-center justify-between p-4 border rounded-md">
          <div className="space-y-0.5">
            <Label htmlFor="account-status" className="text-base font-medium">
              {editConfig.accountStatus?.label ?? 'Account Status'}
            </Label>
            <p className="text-sm text-muted-foreground">
              {isAccountEnabled
                ? (editConfig.accountStatus?.activeMessage ?? 'Account is active')
                : (editConfig.accountStatus?.inactiveMessage ?? 'Account is inactive')}
            </p>
          </div>
          <Switch
            id="account-status"
            checked={isAccountEnabled}
            onCheckedChange={setIsAccountEnabled}
          />
        </div>
      )}

      {/* Resend Invitation Button (for pending/sent invitations) */}
      {showResendInvitation && (
        <div className="flex items-center justify-between p-4 border rounded-md bg-blue-50">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">
              {editConfig.invitationStatus?.label ?? 'Invitation Status'}
            </Label>
            <p className="text-sm text-muted-foreground">
              {currentInvitationStatus === InvitationStatus.Pending
                ? (editConfig.invitationStatus?.pendingMessage ?? 'User invitation is pending')
                : currentInvitationStatus === InvitationStatus.Expired
                  ? 'User invitation has expired'
                  : (editConfig.invitationStatus?.sentMessage ?? 'User invitation is sent')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleResendInvitation}
            disabled={resendInvitation.isPending}
          >
            {resendInvitation.isPending
              ? (editConfig.resendInvitationButton?.loadingLabel ?? 'Sending...')
              : (editConfig.resendInvitationButton?.label ?? 'Resend Invitation')}
          </Button>
        </div>
      )}

      {/* Form Groups */}
      {groups.map((group) => {
        const groupControls = controlsByGroup.get(group.formGroupName) || []

        return (
          <div key={group.formGroupName} className="space-y-4">
            {groupControls.map(renderControlField)}
          </div>
        )
      })}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? '',
          variant: 'outline',
          type: 'button',
          onClick: onCancel,
        })}

        {renderButtonControl({
          label: submitLabel
            ? submitLabel
            : upsertUser.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (FORM_CONFIG.submitButton?.label ?? ''),
          variant: 'default',
          type: 'submit',
          // Block Save until memberships have loaded, so org removal can resolve membershipNumbers.
          disabled: upsertUser.isPending || membershipsLoading,
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <UserCog className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}
